import React, { useState } from 'react';
import { X, Plus, Trash2, DollarSign } from 'lucide-react';

// Default prices for extra activities (can be overridden)
const DEFAULT_ACTIVITY_PRICES = {
  'grocery-stop': 10,
  'city-tour': 50,
  'private-tour': 75,
  'extra-stop': 15,
  'waiting-time': 20
};

const ACTIVITY_LABELS = {
  'grocery-stop': 'Grocery Stop',
  'city-tour': 'City Tour',
  'private-tour': 'Private Tour',
  'extra-stop': 'Extra Stop',
  'waiting-time': 'Waiting Time'
};

const ManualRideForm = ({ isOpen, onClose, onSubmit, selectedDate }) => {
  const [formData, setFormData] = useState({
    passengerName: '',
    passengerPhone: '',
    pickupLocation: '',
    dropoffLocation: '',
    serviceType: 'airport-transfer',
    date: selectedDate || new Date().toISOString().split('T')[0],
    time: '12:00',
    actualFare: '',
    paymentMethod: 'Cash',
    passengers: 1,
    luggage: 0,
    notes: '',
    bookingSource: 'manual'
  });

  const [extraActivities, setExtraActivities] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const addExtraActivity = (type) => {
    setExtraActivities(prev => [
      ...prev,
      {
        type,
        cost: DEFAULT_ACTIVITY_PRICES[type],
        notes: ''
      }
    ]);
  };

  const updateActivityCost = (index, cost) => {
    setExtraActivities(prev => {
      const updated = [...prev];
      updated[index].cost = parseFloat(cost) || 0;
      return updated;
    });
  };

  const updateActivityNotes = (index, notes) => {
    setExtraActivities(prev => {
      const updated = [...prev];
      updated[index].notes = notes;
      return updated;
    });
  };

  const removeActivity = (index) => {
    setExtraActivities(prev => prev.filter((_, i) => i !== index));
  };

  const calculateTotalWithExtras = () => {
    const baseFare = parseFloat(formData.actualFare) || 0;
    const extrasCost = extraActivities.reduce((sum, act) => sum + (act.cost || 0), 0);
    return baseFare + extrasCost;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.passengerName || !formData.passengerPhone || !formData.pickupLocation || 
        !formData.dropoffLocation || !formData.actualFare) {
      alert('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    
    try {
      const submitData = {
        ...formData,
        actualFare: parseFloat(formData.actualFare),
        passengers: parseInt(formData.passengers),
        luggage: parseInt(formData.luggage),
        extraActivities: extraActivities.map(act => ({
          type: act.type,
          cost: parseFloat(act.cost) || 0,
          notes: act.notes || ''
        }))
      };

      await onSubmit(submitData);
      
      // Reset form
      setFormData({
        passengerName: '',
        passengerPhone: '',
        pickupLocation: '',
        dropoffLocation: '',
        serviceType: 'airport-transfer',
        date: selectedDate || new Date().toISOString().split('T')[0],
        time: '12:00',
        actualFare: '',
        paymentMethod: 'Cash',
        passengers: 1,
        luggage: 0,
        notes: '',
        bookingSource: 'manual'
      });
      setExtraActivities([]);
      onClose();
    } catch (error) {
      console.error('Error submitting manual ride:', error);
      alert('Failed to create ride: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-cyan-500 to-blue-500">
          <h2 className="text-2xl font-bold text-white">Add Completed Ride</h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Customer Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Customer Name *
                </label>
                <input
                  type="text"
                  name="passengerName"
                  value={formData.passengerName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Customer Phone *
                </label>
                <input
                  type="tel"
                  name="passengerPhone"
                  value={formData.passengerPhone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  placeholder="+1-242-xxx-xxxx"
                />
              </div>
            </div>
          </div>

          {/* Trip Details */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Trip Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pickup Location *
                </label>
                <input
                  type="text"
                  name="pickupLocation"
                  value={formData.pickupLocation}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  placeholder="LPIA Airport"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dropoff Location *
                </label>
                <input
                  type="text"
                  name="dropoffLocation"
                  value={formData.dropoffLocation}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  placeholder="Atlantis Resort"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Service Type *
                </label>
                <select
                  name="serviceType"
                  value={formData.serviceType}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                >
                  <option value="airport-transfer">Airport Transfer</option>
                  <option value="hotel-transfer">Hotel Transfer</option>
                  <option value="city-tour">City Tour</option>
                  <option value="private-tour">Private Tour</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Booking Source *
                </label>
                <select
                  name="bookingSource"
                  value={formData.bookingSource}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                >
                  <option value="manual">Manual Entry</option>
                  <option value="phone">Phone Call</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="website">Website</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date *
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Time *
                </label>
                <input
                  type="time"
                  name="time"
                  value={formData.time}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Passengers *
                </label>
                <input
                  type="number"
                  name="passengers"
                  value={formData.passengers}
                  onChange={handleChange}
                  min="1"
                  max="15"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Luggage
                </label>
                <input
                  type="number"
                  name="luggage"
                  value={formData.luggage}
                  onChange={handleChange}
                  min="0"
                  max="20"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Payment Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Base Fare * ($)
                </label>
                <input
                  type="number"
                  name="actualFare"
                  value={formData.actualFare}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  placeholder="25.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Method *
                </label>
                <select
                  name="paymentMethod"
                  value={formData.paymentMethod}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                >
                  <option value="Cash">Cash</option>
                  <option value="Credit Card">Credit Card</option>
                  <option value="Debit Card">Debit Card</option>
                </select>
              </div>
            </div>
          </div>

          {/* Extra Activities */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Extra Billable Activities</h3>
              <div className="flex gap-2 flex-wrap">
                {Object.keys(DEFAULT_ACTIVITY_PRICES).map(type => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => addExtraActivity(type)}
                    className="flex items-center space-x-1 px-3 py-1 bg-cyan-100 text-cyan-700 rounded-lg hover:bg-cyan-200 transition-colors text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    <span>{ACTIVITY_LABELS[type]}</span>
                  </button>
                ))}
              </div>
            </div>

            {extraActivities.length > 0 && (
              <div className="space-y-3">
                {extraActivities.map((activity, index) => (
                  <div key={index} className="flex items-start gap-3 bg-white p-3 rounded-lg border border-gray-200">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 mb-2">
                        {ACTIVITY_LABELS[activity.type]}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Cost ($)</label>
                          <input
                            type="number"
                            value={activity.cost}
                            onChange={(e) => updateActivityCost(index, e.target.value)}
                            min="0"
                            step="0.01"
                            className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Notes (optional)</label>
                          <input
                            type="text"
                            value={activity.notes}
                            onChange={(e) => updateActivityNotes(index, e.target.value)}
                            className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                            placeholder="Details..."
                          />
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeActivity(index)}
                      className="text-red-600 hover:text-red-700 p-2"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}

                <div className="flex justify-end pt-2 border-t border-gray-200">
                  <div className="text-right">
                    <div className="text-sm text-gray-600">Base Fare: ${parseFloat(formData.actualFare || 0).toFixed(2)}</div>
                    <div className="text-sm text-gray-600">Extras: ${extraActivities.reduce((sum, act) => sum + (act.cost || 0), 0).toFixed(2)}</div>
                    <div className="text-lg font-bold text-gray-900 flex items-center">
                      <DollarSign className="w-5 h-5" />
                      Total: ${calculateTotalWithExtras().toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes (Optional)
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              placeholder="Any additional notes about this ride..."
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Saving...' : 'Save Completed Ride'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManualRideForm;
